/**
 * <enquiry-popup> — the bespoke commission questionnaire.
 *
 * Markup: sections/enquiry-popup.liquid, which also documents the trigger
 * contract. Any control carrying `data-enquiry-trigger` opens this.
 *
 * THE THEME'S THIRD MODAL, and deliberately the same shape as
 * assets/image-lightbox.js: page-inert for focus containment, counted scroll
 * lock, `inert` in the markup for the closed state, focus to the panel rather
 * than to a control. Where it differs, the difference is commented.
 *
 * LOAD ORDER: after image-lightbox.js. Opening this closes all six other
 * overlays by calling their `close()` methods directly, so those elements have
 * to be upgraded first.
 *
 * ---------------------------------------------------------------------------
 * TWO REAL DEPARTURES FROM THE LIGHTBOX
 * ---------------------------------------------------------------------------
 *
 * 1. TRIGGERS ARE BOUND BY DELEGATION ON `document`, not by a NodeList captured
 *    at connect. The sections that open this do not exist yet, and in the theme
 *    editor they will be added, removed and re-rendered long after this element
 *    has upgraded. A captured list would miss every one of them. ARIA still
 *    needs a per-element pass, so `enhanceTriggers()` runs at connect and again
 *    on `shopify:section:load`.
 *
 * 2. IT OWNS A FORM, so it has a step machine and per-step validation. See
 *    `setStep` and `validateStep`.
 *
 * ---------------------------------------------------------------------------
 * WHY THE MARKUP SHOWS EVERY STEP AND THIS FILE HIDES THEM
 * ---------------------------------------------------------------------------
 *
 * The Liquid renders all questions visible with real `required` attributes, and
 * this script collapses that to one at a time on connect. That ordering is what
 * makes both paths correct at once:
 *
 * - Script runs: one question at a time, `form.noValidate` set at RUNTIME, and
 *   validation done per step with failures announced as text.
 * - Script fails to load: a plain long form with the browser's own validation
 *   intact. Nothing is hidden, so there is no "an invalid form control is not
 *   focusable" error — the failure mode that makes naive multi-step forms
 *   unsubmittable.
 *
 * `noValidate` is set here rather than in the markup for the same reason
 * assets/newsletter-form.js sets it here: if this file never arrives, the
 * browser's validation is still there as a worse-but-real fallback.
 *
 * ---------------------------------------------------------------------------
 * HIDDEN STEPS USE `hidden`, NEVER `disabled`
 * ---------------------------------------------------------------------------
 *
 * A disabled control is EXCLUDED FROM FormData. Disabling the steps the visitor
 * has already answered would silently drop those answers and the merchant would
 * receive an incomplete enquiry with no error anywhere. This is the single most
 * dangerous trap in this component and it is why `hidden` + `inert` is the pair
 * used throughout — `hidden` to remove it visually, `inert` because off-screen
 * is not hidden from focus or from a screen reader.
 */
/**
 * THE MAGIC HREF. Any link whose hash is this opens the popup.
 *
 * It exists because `data-enquiry-trigger` only reaches markup we control, and
 * the two places a merchant most wants this link are ones we do not: body copy
 * typed into a richtext field, and a navigation menu item. Neither can carry a
 * data attribute, but both can carry an href.
 *
 * Matched on the link's PARSED `.hash`, never on the raw href string. Shopify's
 * URL picker hands back a full path, so the same intent arrives as `#enquiry`
 * from a richtext editor and as `/pages/bespoke#enquiry` from a picker, and only
 * the parsed form treats those as the same thing.
 */
const ENQUIRY_HASH = '#enquiry';

if (!customElements.get('enquiry-popup')) {
  customElements.define(
    'enquiry-popup',
    class EnquiryPopup extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-enquiry-panel]');
        this.scrim = this.querySelector('[data-enquiry-scrim]');
        this.form = this.querySelector('form');
        this.thanks = this.querySelector('[data-enquiry-thanks]');
        this.stepsContainer = this.querySelector('[data-enquiry-steps]');
        this.steps = Array.from(this.querySelectorAll('[data-enquiry-step]'));

        if (!this.panel || !this.form || !this.steps.length) {
          console.warn('[enquiry-popup] markup is incomplete; popup disabled');
          return;
        }

        this.isOpen = false;
        this.lastTrigger = null;
        this.stepIndex = 0;

        /** Only the elements WE made inert, so closing never clears someone else's. */
        this.inerted = [];

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleFormClick = this.handleFormClick.bind(this);
        this.handleFormKeydown = this.handleFormKeydown.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.enhanceTriggers = this.enhanceTriggers.bind(this);

        /**
         * Runtime, not markup — see the note at the top of this file. From here
         * on this component owns validation, because a native bubble attached to
         * a `display: none` control is invisible.
         */
        this.form.noValidate = true;

        /**
         * The Next and Back controls ship `hidden` and are revealed only now, the
         * same contract sections/film-strip.liquid uses for its arrows: a control
         * that does nothing without JS is never offered. Reaching this line is
         * the proof that the script bound successfully.
         */
        this.querySelectorAll('[data-enquiry-next], [data-enquiry-back]').forEach((control) => {
          control.hidden = false;
        });

        this.stack();
        this.setStep(0, { focus: false });

        document.addEventListener('click', this.handleTriggerClick);
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('shopify:section:load', this.enhanceTriggers);

        this.form.addEventListener('click', this.handleFormClick);
        this.form.addEventListener('keydown', this.handleFormKeydown);
        this.form.addEventListener('submit', this.handleSubmit);

        /**
         * CLICK-OUTSIDE IS ONE LISTENER ON THE SCRIM, not a document-level
         * handler doing geometry. The scrim IS everything the visitor can see
         * that is not the card. This works only because the panel above it is
         * pointer-events-none — see the note in the Liquid.
         */
        if (this.scrim) this.scrim.addEventListener('click', this.handleCloseClick);

        this.querySelectorAll('[data-enquiry-close]').forEach((button) => {
          button.addEventListener('click', this.handleCloseClick);
        });

        this.enhanceTriggers();
        this.handlePosted();
        this.handleHash();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleTriggerClick);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('shopify:section:load', this.enhanceTriggers);

        this.form?.removeEventListener('click', this.handleFormClick);
        this.form?.removeEventListener('keydown', this.handleFormKeydown);
        this.form?.removeEventListener('submit', this.handleSubmit);
        this.scrim?.removeEventListener('click', this.handleCloseClick);

        /**
         * Torn down while open — the theme editor re-rendering this section is
         * the realistic case — would otherwise leave the page scroll-locked and
         * inert, with nothing left alive to undo it.
         */
        if (this.isOpen) {
          this.releaseScroll();
          this.setPageInert(false);
          this.removeAttribute('data-open');
        }
      }

      /**
       * Re-open on the thank-you panel after a successful submit.
       *
       * DECIDING WHETHER *THIS* FORM IS THE ONE THAT POSTED IS THE WHOLE
       * DIFFICULTY. Shopify sets `?contact_posted=true` for any contact form, and
       * `form.posted_successfully?` is request-scoped to the form TYPE — so on
       * /pages/contact, Liquid cannot tell this popup's submit from the inline
       * contact form's.
       *
       * Two signals, either of which is sufficient:
       *
       * 1. The fragment. The form's action carries `#EnquiryPopup-<id>`, and a
       *    browser carries an action's fragment onto a redirect target that has
       *    none. Exact, when it survives.
       * 2. Being the only contact form in the document. If the fragment did not
       *    survive, then on every page except /pages/contact this popup is the
       *    only thing that could have posted, so opening is correct.
       *
       * Belt and braces deliberately: (1) alone would fail silently and show the
       * visitor nothing if the fragment turns out not to survive on this store —
       * which is an open QA question, not a settled fact.
       */
      handlePosted() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('contact_posted') !== 'true') return;

        const fragmentMatches = window.location.hash === '#' + this.id;
        const onlyContactForm =
          document.querySelectorAll('form[action*="/contact"]').length === 1;

        if (!fragmentMatches && !onlyContactForm) return;

        this.showThanks();
        this.open(null);
      }

      /**
       * Open on arrival when the URL carries the magic hash.
       *
       * This is the other half of the magic href: a link from another page, an
       * email or a menu navigates here normally, and this is what makes the
       * popup appear once the page has loaded.
       *
       * Runs AFTER handlePosted, and does not fight it — `open()` returns early
       * when the popup is already open, and the two hashes are different
       * (`#enquiry` versus `#EnquiryPopup-<id>`), so a post-submit return can
       * never be mistaken for an arrival.
       *
       * Quiet, because nobody clicked anything on THIS page — there is no
       * pointer or keyboard interaction here to read an intent from.
       */
      handleHash() {
        if ((window.location.hash || '').toLowerCase() !== ENQUIRY_HASH) return;
        this.open(null, true);
      }

      /** Reveal the thank-you panel and take the questions out of the flow. */
      showThanks() {
        if (!this.thanks) return;
        this.form.hidden = true;
        this.form.setAttribute('inert', '');
        this.thanks.hidden = false;
      }

      /**
       * ARIA IS SET HERE, NOT IN LIQUID. A trigger with no script behind it does
       * nothing at all, and announcing a dialog that will never open is worse
       * than announcing nothing — worse for exactly the visitors who can least
       * afford it. Same call assets/image-lightbox.js makes.
       *
       * Re-run on `shopify:section:load` because the sections that carry these
       * triggers are added and re-rendered by the theme editor after this element
       * has already upgraded.
       */
      /**
       * Every control that opens this popup, however it was marked up.
       *
       * The attribute selector is a cheap pre-filter — `#not-enquiry` cannot end
       * in `#enquiry`, so it is precise enough — and `magicLink()` then confirms
       * each one against the parsed hash. Cross-page magic links are EXCLUDED:
       * they navigate rather than open, so announcing them as dialog triggers
       * would be a lie.
       */
      allTriggers() {
        const found = new Set(document.querySelectorAll('[data-enquiry-trigger]'));

        document.querySelectorAll('a[href$="#enquiry" i]').forEach((link) => {
          const magic = this.magicLink(link);
          if (magic && this.opensInPlace(magic)) found.add(magic);
        });

        return found;
      }

      enhanceTriggers() {
        this.allTriggers().forEach((trigger) => {
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-expanded', String(this.isOpen));
          trigger.setAttribute('aria-controls', this.id);
        });
      }

      /**
       * @param {HTMLElement|null} trigger - The control that opened it, for focus return.
       * @param {boolean} [quiet] - Suppress the answer field's focus ring. Defaults
       *   to true, because the only callers that pass nothing are scripted ones.
       */
      open(trigger, quiet) {
        if (this.isOpen) return;

        this.setQuiet(quiet !== false);

        /**
         * ORDER IS LOAD-BEARING, for the reason assets/image-lightbox.js records:
         * another overlay's setPageInert() may have made every body child inert
         * except its own and the header's — and this element is its own body
         * child in the flat DOM shape, so it would be inert too. Closing them
         * first restores those attributes before we set ours.
         */
        document.querySelector('mobile-menu')?.close?.();
        document.querySelector('mega-menu')?.close?.();
        document.querySelector('search-overlay')?.close?.();
        document.querySelector('cart-drawer')?.close?.();
        document.querySelector('filter-drawer')?.close?.();
        document.querySelector('image-lightbox')?.close?.();

        this.lastTrigger = trigger;
        this.setOpen(true);

        // The PANEL, not a control — see the note in the Liquid.
        this.panel.focus();
      }

      close() {
        if (!this.isOpen) return;

        this.setOpen(false);

        /**
         * Drop the magic hash once the visitor has dismissed the popup, so that
         * reloading or sharing the URL does not reopen something they closed.
         * `replaceState` rather than assigning `location.hash`: assigning would
         * push a history entry, making Back appear to do nothing.
         *
         * Only ever OUR hash. Anything else on the URL is someone else's.
         */
        if ((window.location.hash || '').toLowerCase() === ENQUIRY_HASH) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        if (this.lastTrigger && this.lastTrigger.isConnected) this.lastTrigger.focus();
        this.lastTrigger = null;
      }

      /** The single state funnel. Nothing else touches these attributes. */
      setOpen(next) {
        if (next === this.isOpen) return;
        this.isOpen = next;

        this.toggleAttribute('data-open', next);

        /**
         * The panel is transparent and pointer-events-none when closed, and both
         * of those are still focusable and still read aloud. This is what
         * actually removes it from the tab order and the accessibility tree.
         */
        this.toggleAttribute('inert', !next);

        /** Magic links included — allTriggers() is the one definition of "trigger". */
        this.allTriggers().forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(next));
        });

        this.setPageInert(next);

        if (next) {
          this.lockScroll();
          return;
        }

        this.releaseScroll();
      }

      /**
       * Lay every step over the same grid cell, so the panel is as tall as the
       * TALLEST question and stops resizing as the visitor advances.
       *
       * WHY THIS IS DONE HERE AND NOT IN THE LIQUID: the class list in the markup
       * is also the no-JS layout, where the questions have to stack vertically and
       * be readable one after another. Overlaying them is only correct once this
       * script is present to show one at a time.
       *
       * WHY THE HIDDEN STEPS USE `invisible` AND NOT `hidden`: `display: none`
       * takes an element out of the grid entirely, so it contributes no height and
       * the cell collapses to whichever step is showing — which is the resizing
       * this exists to stop. `visibility: hidden` keeps the box in the layout, so
       * the cell measures the tallest of them. It also removes the element from
       * the tab order and the accessibility tree on its own; `inert` stays as
       * well, because two mechanisms that mean "not here" are cheaper than
       * discovering which one a given browser honours.
       *
       * These classes are safelisted in assets/css-tailwind.css — this file is not
       * scanned by Tailwind, so nothing here compiles on its own.
       */
      stack() {
        if (!this.stepsContainer) return;

        this.stepsContainer.classList.remove('flex', 'flex-col', 'gap-[50px]');
        this.stepsContainer.classList.add('grid');
        this.steps.forEach((step) => step.classList.add('col-start-1', 'row-start-1'));
      }

      /**
       * Show exactly one step.
       *
       * `invisible` AND `inert` together, never `disabled` — see the note at the
       * top of this file. A disabled control is excluded from FormData, so
       * disabling the steps already answered would drop those answers silently.
       *
       * @param {number} index - Zero-based step to show.
       * @param {{focus?: boolean}} [options] - Whether to move focus into the step.
       */
      setStep(index, options) {
        const focus = !options || options.focus !== false;
        const clamped = Math.max(0, Math.min(index, this.steps.length - 1));
        this.stepIndex = clamped;

        this.steps.forEach((step, i) => {
          const active = i === clamped;
          step.classList.toggle('invisible', !active);
          step.toggleAttribute('inert', !active);

          /** Back is meaningless on the first step, and offering it is noise. */
          const back = step.querySelector('[data-enquiry-back]');
          if (back) back.hidden = clamped === 0;
        });

        if (!focus) return;

        const field = this.steps[clamped].querySelector('input, textarea, select');
        field?.focus();
      }

      /**
       * Validate the current step only.
       *
       * Failures are announced as TEXT, not by a native bubble: the browser
       * attaches those to the control, and `reportValidity()` on a control inside
       * a `hidden` ancestor shows nothing at all. Same discipline
       * assets/newsletter-form.js applies with its status line.
       *
       * @returns {boolean} whether the step may be left.
       */
      validateStep(index) {
        const step = this.steps[index];
        if (!step) return true;

        const fields = step.querySelectorAll('input, textarea, select');
        let valid = true;

        fields.forEach((field) => {
          const ok = field.checkValidity();
          if (!ok) valid = false;

          field.setAttribute('aria-invalid', String(!ok));
          this.setFieldError(step, field, ok ? '' : field.validationMessage);
        });

        return valid;
      }

      /**
       * Put (or clear) a message under a field, in a live region created on
       * demand. Created here rather than rendered empty in the Liquid so that the
       * no-JS path ships no empty alert regions.
       */
      setFieldError(step, field, message) {
        let region = step.querySelector('[data-enquiry-error]');

        if (!message) {
          if (region) region.textContent = '';
          return;
        }

        if (!region) {
          region = document.createElement('p');
          region.setAttribute('data-enquiry-error', '');
          region.setAttribute('role', 'alert');
          /** Safelisted in assets/css-tailwind.css — this file is not scanned. */
          region.className = 'type-body-sm mt-[10px] italic';
          field.insertAdjacentElement('afterend', region);
        }

        region.textContent = message;
      }

      /**
       * Suppress or restore the focus ring on the answer fields.
       *
       * This component moves focus by script on open and on every step, and a
       * scripted `.focus()` is matched by `:focus-visible` far more eagerly than a
       * real click — so without this a mouse user gets a ring on every question
       * they pass through. The CSS lives in the section's stylesheet block, which
       * documents the full reasoning.
       *
       * @param {boolean} on - true to suppress the ring.
       */
      setQuiet(on) {
        this.toggleAttribute('data-quiet', on);
      }

      /**
       * Did this event come from a pointer rather than the keyboard?
       *
       * `detail` is the click count, and it is 0 for a click SYNTHESISED by Enter
       * or Space on a button — which is how the platform lets you tell the two
       * apart. Same test assets/search-overlay.js uses.
       */
      isPointerEvent(event) {
        return !!event && event.detail !== 0;
      }

      next() {
        if (!this.validateStep(this.stepIndex)) {
          this.steps[this.stepIndex].querySelector(':invalid')?.focus();
          return;
        }
        this.setStep(this.stepIndex + 1);
      }

      back() {
        this.setStep(this.stepIndex - 1);
      }

      /**
       * Delegated inside the form, so steps added or re-rendered by the theme
       * editor need no rebinding.
       */
      handleFormClick(event) {
        const next = event.target.closest('[data-enquiry-next]');
        const back = event.target.closest('[data-enquiry-back]');
        if (!next && !back) return;

        event.preventDefault();

        /**
         * Decided BEFORE the step changes, because setStep focuses the new field
         * and the attribute has to already be right when it does.
         */
        this.setQuiet(this.isPointerEvent(event));

        if (next) {
          this.next();
          return;
        }
        this.back();
      }

      /**
       * Enter advances rather than submitting, on every step but the last.
       *
       * Without this, Enter in a single-line field submits the whole form from
       * step one — the browser's default for a form with a submit button — and
       * the visitor sends a near-empty enquiry. A textarea is left alone, where
       * Enter means a new line.
       */
      handleFormKeydown(event) {
        /**
         * TYPING DOES NOT RESTORE THE FOCUS RING, and that is deliberate. An
         * earlier version cleared the quiet state on any keypress in the form, on
         * the reasoning that a key means a keyboard. It does not: someone who
         * clicked into a field is typing into a field they can see, and a ring
         * appearing under their cursor mid-sentence is the same unasked-for
         * indicator the quiet state exists to remove.
         *
         * TAB is the signal, and it is handled on the document listener — it
         * bubbles there from inside the form like anything else. Tab means moving
         * between controls without a pointer, which is when the indicator is
         * actually load-bearing. Enter on a Next or Back BUTTON also restores it,
         * via the `detail === 0` test in handleFormClick.
         */
        if (event.key !== 'Enter') return;
        if (event.target.tagName === 'TEXTAREA') return;
        if (this.stepIndex >= this.steps.length - 1) return;

        event.preventDefault();
        this.next();
      }

      /**
       * The final submit is NATIVE — a real page POST to Shopify's contact
       * endpoint — and that is a deliberate choice rather than an unfinished one.
       *
       * A fetch would have to be right about four things Shopify does not
       * document and that cannot be established from the codebase: where the 302
       * lands, whether hCaptcha's token is in the form when FormData snapshots
       * it, what a /challenge bounce looks like to fetch, and where the rate
       * limit sits. Being wrong about any of them loses an enquiry silently,
       * which is the one failure this component must not have. The cost is one
       * reload after the last answer; the popup re-opens on the thank-you panel
       * via handlePosted().
       *
       * All this handler does is re-check every step and send the visitor back to
       * the first one that fails, since only the last step was validated by next().
       */
      handleSubmit(event) {
        for (let i = 0; i < this.steps.length; i += 1) {
          if (this.validateStep(i)) continue;

          event.preventDefault();
          this.setStep(i);
          this.steps[i].querySelector(':invalid')?.focus();
          return;
        }
      }

      /**
       * Make every top-level region of the page inert except this one.
       *
       * A focus trap without a focus trap: no keydown interception, no
       * first/last-focusable bookkeeping that goes stale. The browser simply has
       * nowhere else to send focus.
       *
       * ONE ELEMENT STAYS LIVE, where assets/cart-drawer.js keeps two. There is
       * nothing in the header a visitor needs while filling in a questionnaire,
       * and leaving it live would let them open the cart underneath this.
       *
       * Elements already inert are left alone and not recorded, so this composes
       * with another overlay's inerting instead of stealing its state.
       */
      setPageInert(on) {
        if (!on) {
          this.inerted.forEach((element) => element.removeAttribute('inert'));
          this.inerted = [];
          return;
        }

        const keep = this.topLevelAncestor(this);

        Array.prototype.forEach.call(document.body.children, (element) => {
          if (element === keep) return;
          if (element.hasAttribute('inert')) return;

          element.setAttribute('inert', '');
          this.inerted.push(element);
        });
      }

      /** Walk up to whichever child of <body> contains the given node. */
      topLevelAncestor(node) {
        while (node.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }
        return node;
      }

      /**
       * Through the shared controller, per CLAUDE.md — it owns the single Lenis
       * instance and COUNTS locks, so this opening over another overlay cannot
       * unlock the page underneath when it closes.
       *
       * The direct fallback exists because the whole point of the controller is
       * that it might not be there: assets/scroll.js is deferred and can fail to
       * load. Without it the page scrolls behind an open overlay.
       */
      lockScroll() {
        if (window.themeScroll) {
          this.lockedController = true;
          window.themeScroll.lock('enquiry-popup');
          return;
        }

        this.lockedController = false;
        document.documentElement.classList.add('overflow-hidden');
      }

      releaseScroll() {
        if (this.lockedController && window.themeScroll) {
          window.themeScroll.unlock('enquiry-popup');
        } else {
          document.documentElement.classList.remove('overflow-hidden');
        }
        this.lockedController = false;
      }

      /**
       * The link this event landed on, if it is a magic one. Otherwise null.
       *
       * `.hash` is read off the ANCHOR ELEMENT, which the browser has already
       * resolved against the document — so a bare `#enquiry` typed into a
       * richtext field and a `/pages/bespoke#enquiry` from Shopify's URL picker
       * both come back as `#enquiry`, which is the whole point.
       */
      magicLink(node) {
        const link = node.closest ? node.closest('a[href]') : null;
        if (!link) return null;
        if ((link.hash || '').toLowerCase() !== ENQUIRY_HASH) return null;
        return link;
      }

      /**
       * Does this link point at the page we are already on?
       *
       * A magic link to the CURRENT page opens the popup in place. One pointing
       * at a DIFFERENT page is left alone to navigate, and `handleHash()` opens
       * the popup when the visitor arrives — because "take me there and open it"
       * is a real thing to want from a footer or a menu, and hijacking it here
       * would strand the visitor on the wrong page with a modal over it.
       */
      opensInPlace(link) {
        return link.host === window.location.host && link.pathname === window.location.pathname;
      }

      handleTriggerClick(event) {
        let trigger = event.target.closest('[data-enquiry-trigger]');

        if (!trigger) {
          const magic = this.magicLink(event.target);
          if (magic && this.opensInPlace(magic)) trigger = magic;
        }

        if (!trigger) return;

        /**
         * Modified clicks are let through untouched. On a <button> they do
         * nothing anyway, but the guard costs one line and survives someone later
         * deciding a trigger should be an <a>.
         */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        event.preventDefault();
        this.open(trigger, this.isPointerEvent(event));
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.close();
      }

      handleKeydown(event) {
        if (!this.isOpen) return;

        /**
         * THE MOMENT THE VISITOR TABS, THEY ARE ON THE KEYBOARD. Caught here as
         * well as in the form's own handler, because Tab from the Close button —
         * which sits outside the form — is the first thing a keyboard user does
         * after opening, and the ring has to be back before they land on a field.
         */
        if (event.key === 'Tab') {
          this.setQuiet(false);
          return;
        }

        if (event.key !== 'Escape') return;
        this.close();
      }
    }
  );
}
